import * as THREE from "three";
import { initAudio } from "./audio";

export interface CarControlsState {
  forward: boolean;
  backward: boolean;
  left: boolean;
  right: boolean;
  brake: boolean;
  /** Edge-triggered — set true on Space keydown, consumed (reset) by whoever reads it. */
  jump: boolean;
}

export const controlsState: CarControlsState = {
  forward: false,
  backward: false,
  left: false,
  right: false,
  brake: false,
  jump: false,
};

/** Shared world-space position of whichever entity (car/character) is active — read by
 * proximity-based ambient sounds (waterfall, crowd chatter) that don't otherwise know
 * where the player is. */
export const playerPosition = new THREE.Vector3(0, 0, 0);

/**
 * Clears all movement input. controlsState is module-level and outlives the World
 * component — if a key is still physically held when the player navigates away
 * (e.g. clicking a button instead of releasing the key), the keyup never fires
 * because the keyboard listener was detached, leaving that input stuck "on" for
 * whenever World mounts again. Call this on mount to guarantee a clean slate.
 */
export const resetControls = () => {
  controlsState.forward = false;
  controlsState.backward = false;
  controlsState.left = false;
  controlsState.right = false;
  controlsState.brake = false;
  controlsState.jump = false;
  mouseState.rightDown = false;
};

const keyMap: Record<string, keyof CarControlsState> = {
  KeyW: "forward",
  ArrowUp: "forward",
  KeyS: "backward",
  ArrowDown: "backward",
  KeyA: "left",
  ArrowLeft: "left",
  KeyD: "right",
  ArrowRight: "right",
  Space: "brake",
};

let keyboardListenersAttached = false;

export const attachKeyboardControls = (): (() => void) => {
  if (keyboardListenersAttached) return () => {};
  keyboardListenersAttached = true;

  const handleKeyDown = (e: KeyboardEvent) => {
    const control = keyMap[e.code];
    if (control) {
      controlsState[control] = true;
      if (e.code === "Space") {
        e.preventDefault();
        if (!e.repeat) controlsState.jump = true;
      }
    }
  };

  const handleKeyUp = (e: KeyboardEvent) => {
    const control = keyMap[e.code];
    if (control) controlsState[control] = false;
  };

  window.addEventListener("keydown", handleKeyDown);
  window.addEventListener("keyup", handleKeyUp);

  return () => {
    window.removeEventListener("keydown", handleKeyDown);
    window.removeEventListener("keyup", handleKeyUp);
    keyboardListenersAttached = false;
  };
};

export const setTouchControl = (
  key: keyof CarControlsState,
  pressed: boolean,
) => {
  controlsState[key] = pressed;
};

// ---- Mouse look + right-click-to-move ----

export interface MouseLookState {
  orbitYaw: number; // radians — shared facing/camera azimuth
  orbitPitchT: number; // 0..1, camera elevation blend
  pointerLocked: boolean;
  rightDown: boolean;
  invertY: boolean;
}

const INVERT_Y_STORAGE_KEY = "world_invert_mouse_y";

/**
 * Browsers don't expose the OS "invert mouse" preference directly, so we use
 * the platform's own convention as the system-appropriate default: macOS
 * ships with "natural" (content-follows-finger) scrolling/look enabled by
 * default, which reads as inverted-Y for camera pitch; other platforms
 * default to non-inverted. A user override, once set, always wins.
 */
const detectSystemDefaultInvertY = (): boolean => {
  if (typeof navigator === "undefined") return false;
  const platform = (navigator.platform || (navigator as any).userAgentData?.platform || "").toLowerCase();
  const ua = navigator.userAgent.toLowerCase();
  return platform.includes("mac") || ua.includes("mac os");
};

const readStoredInvertY = (): boolean | null => {
  try {
    const stored = localStorage.getItem(INVERT_Y_STORAGE_KEY);
    if (stored === "true") return true;
    if (stored === "false") return false;
  } catch {
    /* localStorage unavailable — fall through to system default */
  }
  return null;
};

export const mouseState: MouseLookState = {
  orbitYaw: 0,
  orbitPitchT: 0.45,
  pointerLocked: false,
  rightDown: false,
  invertY: readStoredInvertY() ?? detectSystemDefaultInvertY(),
};

export const setInvertY = (value: boolean) => {
  mouseState.invertY = value;
  try {
    localStorage.setItem(INVERT_Y_STORAGE_KEY, String(value));
  } catch {
    /* ignore persistence failures (private mode, etc.) */
  }
};

const YAW_SENSITIVITY = 0.0024;
const PITCH_SENSITIVITY = 0.0016;

/** Attaches pointer-lock mouse-look + right-click-forward to a canvas element. */
export const attachMouseControls = (canvas: HTMLElement): (() => void) => {
  const handleMouseMove = (e: MouseEvent) => {
    if (!mouseState.pointerLocked) return;
    mouseState.orbitYaw -= e.movementX * YAW_SENSITIVITY;
    const pitchDelta = e.movementY * PITCH_SENSITIVITY * (mouseState.invertY ? -1 : 1);
    mouseState.orbitPitchT = THREE.MathUtils.clamp(
      mouseState.orbitPitchT - pitchDelta,
      0,
      1,
    );
  };

  const handleMouseDown = (e: MouseEvent) => {
    if (e.button === 2) {
      mouseState.rightDown = true;
      e.preventDefault();
    }
    initAudio();
    // Both must be requested synchronously within the same user gesture —
    // chaining one off the other's promise loses activation and gets rejected.
    if (document.pointerLockElement !== canvas) {
      canvas.requestPointerLock?.();
    }
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => {
        /* fullscreen can be denied/unavailable — pointer lock still works without it */
      });
    }
  };

  const handleMouseUp = (e: MouseEvent) => {
    if (e.button === 2) mouseState.rightDown = false;
  };

  const handleContextMenu = (e: Event) => e.preventDefault();

  const handleLockChange = () => {
    mouseState.pointerLocked = document.pointerLockElement === canvas;
    if (!mouseState.pointerLocked) mouseState.rightDown = false;
  };

  canvas.addEventListener("mousedown", handleMouseDown);
  window.addEventListener("mouseup", handleMouseUp);
  window.addEventListener("mousemove", handleMouseMove);
  canvas.addEventListener("contextmenu", handleContextMenu);
  document.addEventListener("pointerlockchange", handleLockChange);

  return () => {
    canvas.removeEventListener("mousedown", handleMouseDown);
    window.removeEventListener("mouseup", handleMouseUp);
    window.removeEventListener("mousemove", handleMouseMove);
    canvas.removeEventListener("contextmenu", handleContextMenu);
    document.removeEventListener("pointerlockchange", handleLockChange);
    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {});
    }
  };
};

/** World-space forward direction for a given yaw (matches an entity with rotation.y = yaw). */
export const yawToForward = (yaw: number): THREE.Vector3 =>
  new THREE.Vector3(-Math.sin(yaw), 0, -Math.cos(yaw));

/** Orbit-camera offset (added to the followed entity's position) for a given yaw/pitch blend. */
export const getOrbitCameraOffset = (yaw: number, pitchT: number): THREE.Vector3 => {
  const height = 2.4 + pitchT * 5.5;
  const dist = 9.5 - pitchT * 4;
  return new THREE.Vector3(Math.sin(yaw) * dist, height, Math.cos(yaw) * dist);
};
